import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  MoreVertical,
  CheckCircle2,
  Users,
  Eye,
  IndianRupee,
  Clock,
  SlidersHorizontal as Filters,
  X,
  Calendar,
  AlertCircle,
  Landmark,
  Trash2
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import LoanDrawer from './LoanDrawer';
import LoanApprovalModal from './LoanApprovalModal';
import styles from './Loans.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import api from '../../../api/axios';
import Pagination from '../../../components/Common/Pagination/Pagination';
import NoData from '../../../components/Common/NoData/NoData';
import { useLoading } from '../../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Loans = () => {
  const navigate = useNavigate();
  const { loading } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = storedUser.role?.toLowerCase() === 'admin';

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });

  const [filters, setFilters] = useState({
    employee_id: '',
    status: ''
  });

  const [filterData, setFilterData] = useState({
    employees: []
  });

  const [loans, setLoans] = useState([]);
  const [metrics, setMetrics] = useState([
    { label: 'Pending Requests', value: '0', icon: <Clock size={22} />, theme: 'orange', highlight: true },
    { label: 'Active Loans', value: '0', icon: <CheckCircle2 size={22} />, theme: 'green' },
    { label: 'Total Loaned', value: '₹0', icon: <IndianRupee size={22} />, theme: 'blue' },
    { label: 'Outstanding Balance', value: '₹0', icon: <AlertCircle size={22} />, theme: 'purple' }
  ]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'defaulted', label: 'Defaulted' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const fetchFilterData = async () => {
    try {
      const response = await api.get('/employees-dropdown');
      setFilterData({
        employees: response.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/loans/dashboard');
      if (response.data.success) {
        const data = response.data.data;
        setMetrics([
          { label: 'Pending Requests', value: (data.pending_requests || '0').toString(), icon: <Clock size={22} />, theme: 'orange', highlight: true },
          { label: 'Active Loans', value: (data.active_loans || '0').toString(), icon: <CheckCircle2 size={22} />, theme: 'green' },
          { label: 'Total Loaned', value: `₹${parseFloat(data.total_amount_loaned || 0).toLocaleString('en-IN')}`, icon: <IndianRupee size={22} />, theme: 'blue' },
          { label: 'Outstanding Balance', value: `₹${parseFloat(data.outstanding_amount || 0).toLocaleString('en-IN')}`, icon: <AlertCircle size={22} />, theme: 'purple' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching loan summary dashboard:', error);
    }
  };

  const fetchLoans = async () => {
    try {
      const response = await api.get('/loans', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          employee_id: filters.employee_id,
          status: filters.status
        }
      });
      if (response.data.success) {
        // Handle laravel pagination data structure
        const resData = response.data.data;
        setLoans(resData.data || []);
        setPaginationData({
          total: resData.total || 0,
          current_page: resData.current_page || 1,
          per_page: resData.per_page || 10,
          last_page: resData.last_page || 1
        });
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      if (error.response?.status === 404) {
        setLoans([]);
        setPaginationData(prev => ({ ...prev, total: 0, last_page: 1 }));
      } else {
        toast.error('Failed to load loans list');
      }
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchLoans();
    fetchDashboard();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      employee_id: '',
      status: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setActiveDropdown(null);
      }
    };

    const handleScroll = () => {
      if (activeDropdown !== null) setActiveDropdown(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeDropdown]);

  const toggleActionMenu = (id, e) => {
    e.stopPropagation();
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setActiveDropdown(id);
    }
  };

  const handleDelete = async (id, loanNumber) => {
    setActiveDropdown(null);
    const result = await MySwal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete Loan ${loanNumber}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/loans/${id}`);
        toast.success('Loan deleted successfully');
        fetchLoans();
        fetchDashboard();
      } catch (error) {
        console.error('Error deleting loan:', error);
        toast.error(error.response?.data?.message || 'Failed to delete loan record');
      }
    }
  };

  const employeeOptions = [
    { value: '', label: 'All Employees' },
    ...filterData.employees.map(e => ({
      value: e.id,
      label: e.employee_name
    }))
  ];

  return (
    <div className={styles['loans-container']}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)} />}
      
      <div className={styles['content-wrapper']}>
        <Header setIsSidebarOpen={setIsSidebarOpen} />
        
        <main className={styles['main-content']}>
          <Breadcrumbs 
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Loan Requests' }
            ]} 
          />
          
          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>Loan Requests</h1>
              <p>Monitor employee loans, EMIs, skip requests, and repayment analytics.</p>
            </div>
            
            <button className={styles['add-btn']} onClick={() => setIsDrawerOpen(true)}>
              <Plus size={18} />
              <span>New Loan Request</span>
            </button>
          </div>

          {/* Metrics summary widgets */}
          <div className={styles['summary-metrics-row']}>
            {metrics.map((m, index) => (
              <div key={index} className={[styles['metric-card'], styles[m.theme], m.highlight ? styles['highlight-card'] : ''].join(' ')}>
                {m.highlight && <div className={styles['glow-effect']} />}
                <div className={styles['metric-icon-box']}>{m.icon}</div>
                <div className={styles['metric-info']}>
                  <span className={styles['metric-label']}>{m.label}</span>
                  <div className={styles['metric-value-group']}>
                    <span className={styles['metric-value']}>{m.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Controls (Search & Filters toggling) */}
          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input 
                type="text" 
                placeholder="Search by Employee or Loan No..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect 
                  options={[
                    { value: 10, label: '10 Per Page' },
                    { value: 25, label: '25 Per Page' },
                    { value: 50, label: '50 Per Page' }
                  ]}
                  value={{ value: itemsPerPage, label: `${itemsPerPage} Per Page` }}
                  onChange={(opt) => { setItemsPerPage(opt.value); setCurrentPage(1); }}
                />
              </div>

              <button 
                className={[styles['filter-btn'], showFilters ? styles['active'] : ''].join(' ')}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={16} />
                <span>Filters</span>
                {(filters.status || filters.employee_id) && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {/* Slide-down filter panel */}
          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Advanced Filtering</h3>
                  <p>Narrow down your loan request dashboard.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={16} />
                </button>
              </div>

              <div className={styles['filters-grid']}>
                {/* Employee Filter */}
                <div className={styles['filter-group']}>
                  <label>Filter by Employee</label>
                  <CustomSelect 
                    options={employeeOptions}
                    value={employeeOptions.find(opt => opt.value === filters.employee_id) || employeeOptions[0]}
                    onChange={(opt) => handleFilterChange('employee_id', opt.value)}
                  />
                </div>

                {/* Status Filter */}
                <div className={styles['filter-group']}>
                  <label>Filter by Status</label>
                  <CustomSelect 
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === filters.status) || statusOptions[0]}
                    onChange={(opt) => handleFilterChange('status', opt.value)}
                  />
                </div>
              </div>

              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset All</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Apply & Close</button>
              </div>
            </div>
          )}

          {/* Premium Table View */}
          <div className={styles['custom-table-container']}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>Loan Details</th>
                    <th>Loan Amount</th>
                    <th>EMI Details</th>
                    <th>Status</th>
                    <th>Expected End</th>
                    <th>Date Raised</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loans.length > 0 ? (
                    loans.map((loan) => (
                      <tr key={loan.id}>
                        <td>
                          <div className={styles['user-identity']}>
                            <div className={styles['avatar-small']}>
                              {(loan.employee?.employee_name || 'E').charAt(0).toUpperCase()}
                            </div>
                            <div className={styles['user-info']}>
                              <span 
                                className={[styles['user-name'], styles['clickable']].join(' ')} 
                                onClick={() => navigate(`/employees/loan-requests/view/${loan.id}`)}
                              >
                                {loan.employee?.employee_name || '---'}
                              </span>
                              <span className={styles['emp-id']}>{loan.loan_number}</span>
                            </div>
                          </div>
                        </td>
                        <td className={styles['amount-cell']}>₹{parseFloat(loan.loan_amount || 0).toLocaleString('en-IN')}</td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: 700 }}>₹{parseFloat(loan.emi_amount || 0).toLocaleString('en-IN')} / mo</span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>{loan.remaining_emis} of {loan.total_emis} EMIs left</span>
                          </div>
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${styles[`status-${loan.status?.toLowerCase()}`]}`}>
                            {loan.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className={styles['date-cell']}>{loan.loan_end_date || 'TBD'}</td>
                        <td className={styles['date-cell']}>{loan.created_at ? new Date(loan.created_at).toLocaleDateString('en-IN') : '---'}</td>
                        <td>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${activeDropdown === loan.id ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(loan.id, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {activeDropdown === loan.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/loan-requests/view/${loan.id}`)}>
                                  <Eye size={16} /> View Details
                                </button>
                                {loan.status === 'pending' && isAdmin && (
                                  <button className={styles['dropdown-item']} style={{ color: '#059669' }} onClick={(e) => { e.stopPropagation(); setSelectedRecord(loan); setIsApprovalModalOpen(true); setActiveDropdown(null); }}>
                                    <CheckCircle2 size={16} /> Approve Loan
                                  </button>
                                )}
                                <div className={styles['dropdown-divider']}></div>
                                <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDelete(loan.id, loan.loan_number)}>
                                  <Trash2 size={16} onClick={(e) => { e.stopPropagation(); handleDelete(loan.id, loan.loan_number); }} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : !loading && (
                    <tr>
                      <td colSpan="7" style={{ padding: '0' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                          <NoData message="No Loan Requests found." isTable={true} />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Responsive Mobile View */}
          <div className={styles['po-cards']}>
            {loans.length > 0 ? (
              loans.map((loan) => (
                <div key={loan.id} className={styles['card']}>
                  <div className={styles['card-header']}>
                    <span className={styles['id-cell']}>{loan.loan_number}</span>
                    <span className={`${styles['status-badge']} ${styles[`status-${loan.status?.toLowerCase()}`]}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px' }}>
                      {loan.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  <div className={styles['card-row']}>
                    <span className={styles['card-label']}>Employee</span>
                    <span className={styles['card-value']}>{loan.employee?.employee_name || '---'}</span>
                  </div>
                  <div className={styles['card-row']}>
                    <span className={styles['card-label']}>Loan Amount</span>
                    <span className={styles['card-value']}>₹{parseFloat(loan.loan_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className={styles['card-row']}>
                    <span className={styles['card-label']}>EMI Amount</span>
                    <span className={styles['card-value']}>₹{parseFloat(loan.emi_amount || 0).toLocaleString('en-IN')} / mo</span>
                  </div>
                  <div className={styles['card-row']}>
                    <span className={styles['card-label']}>Remaining</span>
                    <span className={styles['card-value']}>{loan.remaining_emis} of {loan.total_emis} months</span>
                  </div>
                  <div className={styles['card-row']} style={{ borderBottom: 'none' }}>
                    <span className={styles['card-label']}>Actions</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className={styles['emi-btn']} onClick={() => navigate(`/employees/loan-requests/view/${loan.id}`)}>
                        View Details
                      </button>
                      {loan.status === 'pending' && isAdmin && (
                        <button className={[styles['emi-btn'], styles['double']].join(' ')} onClick={() => { setSelectedRecord(loan); setIsApprovalModalOpen(true); }}>
                          Approve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : !loading && (
              <NoData message="No Loan Requests found." isTable={false} />
            )}
          </div>

          <div className={styles['desktop-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <Footer />
        </main>
      </div>

      <LoanDrawer 
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchLoans();
          fetchDashboard();
        }}
      />

      <LoanApprovalModal 
        isOpen={isApprovalModalOpen}
        onClose={() => setIsApprovalModalOpen(false)}
        loanId={selectedRecord?.id}
        onApproved={() => {
          fetchLoans();
          fetchDashboard();
        }}
      />
    </div>
  );
};

export default Loans;
